import React from 'react';
import { Download, FileText, Filter, Calendar, FileBarChart2 } from 'lucide-react';
import type { FinancialReport } from '../../types/sanity';

interface ReportsListProps {
  reports: FinancialReport[];
  loading: boolean;
  error: string | null;
  selectedYear: number | null;
  setSelectedYear: (year: number | null) => void;
  selectedType: string | null;
  setSelectedType: (type: string | null) => void;
  years: number[];
  reportTypes: string[];
}

export function ReportsList({ 
  reports, 
  loading, 
  error,
  selectedYear,
  setSelectedYear,
  selectedType,
  setSelectedType,
  years,
  reportTypes
}: ReportsListProps) {
  const getReportTypeName = (type: string) => {
    const typeMap: Record<string, string> = {
      'financial': 'Sprawozdanie finansowe',
      'substantive': 'Sprawozdanie z działalności',
      'balance': 'Bilans',
      'profit_loss': 'Rachunek zysków i strat',
      'additional_info': 'Informacja dodatkowa',
      'opp_report': 'Sprawozdanie z działalności OPP',
      'board_resolution': 'Uchwała zarządu',
      'other': 'Inne'
    };
    
    return typeMap[type] || type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
          <FileText className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
        </div>
        <h2 className="card-title">
          Sprawozdania Fundacji
        </h2>
      </div>

      {/* Filters */}
      <div className="mb-8 p-4 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-accessible-gray-600 dark:text-white/90 high-contrast:text-white" />
          <h3 className="heading-5 mb-0">Filtry</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Year filter */}
          <div>
            <label htmlFor="year-filter" className="label-medium mb-2 block">
              Rok
            </label>
            <select
              id="year-filter"
              value={selectedYear || ''}
              onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-4 py-2 border border-accessible-gray-300 dark:border-accessible-gray-600 rounded-lg focus:ring-2 focus:ring-accessible-red-600 focus:border-transparent bg-white dark:bg-accessible-gray-700 text-accessible-gray-900 dark:text-white form-input"
            >
              <option value="">Wszystkie lata</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          {/* Type filter */}
          <div>
            <label htmlFor="type-filter" className="label-medium mb-2 block">
              Typ sprawozdania
            </label>
            <select
              id="type-filter"
              value={selectedType || ''}
              onChange={(e) => setSelectedType(e.target.value || null)}
              className="w-full px-4 py-2 border border-accessible-gray-300 dark:border-accessible-gray-600 rounded-lg focus:ring-2 focus:ring-accessible-red-600 focus:border-transparent bg-white dark:bg-accessible-gray-700 text-accessible-gray-900 dark:text-white form-input"
            >
              <option value="">Wszystkie typy</option>
              {reportTypes.map((type) => (
                <option key={type} value={type}>
                  {getReportTypeName(type)}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {(selectedYear || selectedType) && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setSelectedYear(null);
                setSelectedType(null);
              }}
              className="text-accessible-red-600 hover:text-accessible-red-700 transition-colors text-sm font-medium"
            >
              Wyczyść filtry
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="body-large">Ładowanie sprawozdań...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="body-large text-accessible-red-600">{error}</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12">
          <p className="body-large">Brak sprawozdań spełniających wybrane kryteria.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reports.map((report) => (
            <div 
              key={report._id} 
              className="p-6 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="heading-4 mb-2">
                    {report.title}
                  </h3>
                  <div className="flex flex-wrap gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-accessible-gray-600 dark:text-white/80 high-contrast:text-white" />
                      <span className="body-small text-accessible-gray-600 dark:text-white high-contrast:text-white no-margin filter-value">
                        Rok: {report.year}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileBarChart2 className="w-4 h-4 text-accessible-gray-600 dark:text-white/80 high-contrast:text-white" />
                      <span className="body-small text-accessible-gray-600 dark:text-white high-contrast:text-white no-margin filter-value">
                        {getReportTypeName(report.reportType)}
                      </span>
                    </div>
                  </div>
                  {report.description && (
                    <p className="body-medium mt-2">
                      {report.description}
                    </p>
                  )}
                  <p className="body-small text-accessible-gray-600 dark:text-white high-contrast:text-white mt-2 no-margin">
                    Opublikowano: {formatDate(report.publishedAt)}
                  </p>
                </div>
                
                <a
                  href={report.file?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accessible-red-600 text-white px-4 py-2 rounded-lg hover:bg-accessible-red-700 transition-colors btn-text-medium"
                  title={`Pobierz ${report.title}`}
                >
                  <Download className="w-5 h-5" />
                  <span>Pobierz PDF</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}